/**
 * Upload constraints shared by the client (UX hints + pre-flight validation)
 * and the server (boundary enforcement). Keeping them here means both sides
 * agree on the same limits and the same allow-list.
 *
 * Decisions: max 25 MB per file, at most 5 files per upload, and only PDF /
 * JPEG / PNG / HEIC (HEIC kept to match the oracle — phone-photographed
 * receipts are common).
 */

export const MAX_UPLOAD_FILE_SIZE_BYTES = 25 * 1024 * 1024;
export const MAX_UPLOAD_FILE_COUNT = 5;

/** Human-readable limit for UI hints and validation messages (German UI). */
export const MAX_UPLOAD_FILE_SIZE_LABEL = "25 MB";

/**
 * The most a single upload batch can carry: every slot at the per-file maximum.
 * **Derived**, never hardcoded, so the per-file size, the count and the body limit
 * can never drift apart again — the drift that made a 24 MB file (well under the
 * advertised 25 MB) fail against a 30 MB body limit.
 */
export const MAX_UPLOAD_BATCH_SIZE_BYTES = MAX_UPLOAD_FILE_COUNT * MAX_UPLOAD_FILE_SIZE_BYTES;

/** Human-readable batch limit for UI hints and validation messages (German UI). */
export const MAX_UPLOAD_BATCH_SIZE_LABEL = "125 MB";

/**
 * Body-parser limit for the **authenticated** endpoint, in bytes. Documents travel
 * base64 (×4/3) inside a JSON envelope, so the limit must cover the largest batch
 * plus headroom for the envelope (file names, categories, structure). Derived from
 * the batch size so it tracks the promise automatically. ≈ 175 MB.
 */
export const AUTH_BODY_LIMIT_BYTES = Math.ceil((MAX_UPLOAD_BATCH_SIZE_BYTES * 4) / 3) + 8 * 1024 * 1024;

/**
 * Body-parser limit for the **unauthenticated** endpoint, in bytes. It carries only
 * tiny control payloads (e.g. `ping`) and must never accept an upload-sized body —
 * today it inherits the same limit as the authenticated one for no reason.
 */
export const NO_AUTH_BODY_LIMIT_BYTES = 1 * 1024 * 1024;

/**
 * Office formats are accepted alongside PDF and photos: bank statements and order
 * data arrive as Excel or CSV, contracts as Word. Confirmed as indispensable for
 * daily work at the handover meeting of 12.08.2026, with the macro risk of the
 * legacy `.xls`/`.doc` formats knowingly accepted by the customer.
 *
 * **Macro-enabled formats are deliberately absent** — `.xlsm`/`.docm` are their own
 * formats, not «Excel and Word», and a macro is code. Nothing in this flow needs to
 * carry any.
 */
export const ALLOWED_UPLOAD_MIME_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/heif",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "text/csv",
] as const;
export type AllowedUploadMimeType = (typeof ALLOWED_UPLOAD_MIME_TYPES)[number];

export const ALLOWED_UPLOAD_EXTENSIONS = [
    ".pdf",
    ".jpg",
    ".jpeg",
    ".png",
    ".heic",
    ".heif",
    ".xlsx",
    ".xls",
    ".docx",
    ".doc",
    ".csv",
] as const;

/** Short, user-facing list of accepted types for hints and the file picker. */
export const ALLOWED_UPLOAD_TYPES_LABEL = "PDF, Word, Excel, CSV, JPEG, PNG, HEIC";

/** `accept` attribute value for an `<input type="file">`. */
export const UPLOAD_ACCEPT_ATTRIBUTE = [...ALLOWED_UPLOAD_MIME_TYPES, ...ALLOWED_UPLOAD_EXTENSIONS].join(",");

export type UploadFileRejectionReason = "type" | "size";

export interface UploadFileCandidate {
    fileName: string;
    contentType: string;
    sizeBytes: number;
}

export interface UploadFileRejection {
    fileName: string;
    reason: UploadFileRejectionReason;
}

const extensionOf = (fileName: string): string => {
    const dot = fileName.lastIndexOf(".");
    return dot >= 0 ? fileName.slice(dot).toLowerCase() : "";
};

/**
 * True when the file is an accepted type. Matches on MIME type first; falls
 * back to the extension because browsers report HEIC inconsistently (often an
 * empty content type).
 */
export const isAllowedUploadType = ({ fileName, contentType }: UploadFileCandidate): boolean =>
    (ALLOWED_UPLOAD_MIME_TYPES as readonly string[]).includes(contentType) ||
    (ALLOWED_UPLOAD_EXTENSIONS as readonly string[]).includes(extensionOf(fileName));

/**
 * Returns a rejection if the candidate violates the type or size constraint,
 * else `undefined`. Type is checked before size so a wrong-type file reports
 * the more fundamental problem.
 */
export const checkUploadFile = (file: UploadFileCandidate): UploadFileRejection | undefined => {
    if (!isAllowedUploadType(file)) {
        return { fileName: file.fileName, reason: "type" };
    }
    if (file.sizeBytes > MAX_UPLOAD_FILE_SIZE_BYTES) {
        return { fileName: file.fileName, reason: "size" };
    }
    return undefined;
};

export type UploadBatchRejectionReason = "count" | "batchSize";

/**
 * Returns a rejection if the batch as a whole violates the count or total-size
 * constraint, else `undefined`. Complements {@link checkUploadFile} (which is
 * per-file): a batch of five 25 MB files is exactly {@link MAX_UPLOAD_BATCH_SIZE_BYTES},
 * so the total is what the raised body limit is sized against. Enforced on both
 * sides — the client pre-flight (authoritative, because nginx buffers the whole
 * body before the server can answer) and the server domain.
 */
export const checkUploadBatch = (
    files: readonly Pick<UploadFileCandidate, "sizeBytes">[]
): UploadBatchRejectionReason | undefined => {
    if (files.length > MAX_UPLOAD_FILE_COUNT) {
        return "count";
    }
    const totalBytes = files.reduce((sum, file) => sum + file.sizeBytes, 0);
    if (totalBytes > MAX_UPLOAD_BATCH_SIZE_BYTES) {
        return "batchSize";
    }
    return undefined;
};

/** Map an accepted file extension to the MIME type we store for it. */
const EXTENSION_MIME_TYPES: Readonly<Record<string, AllowedUploadMimeType>> = {
    ".pdf": "application/pdf",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".heic": "image/heic",
    ".heif": "image/heif",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".xls": "application/vnd.ms-excel",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".doc": "application/msword",
    ".csv": "text/csv",
};

/**
 * The content type to *store* for an upload — never the raw client string. If
 * the client-declared type is on the allow-list it is kept as-is; otherwise it
 * is derived from the file extension (browsers report HEIC with an empty or
 * wrong content type, and a `.pdf` could arrive tagged `text/html`). Falls back
 * to `application/octet-stream` so a stored Document can never carry an
 * attacker-chosen renderable type such as `text/html`. Call only on candidates
 * that already passed {@link checkUploadFile}.
 */
export const canonicalUploadContentType = ({
    fileName,
    contentType,
}: Pick<UploadFileCandidate, "fileName" | "contentType">): string => {
    if ((ALLOWED_UPLOAD_MIME_TYPES as readonly string[]).includes(contentType)) {
        return contentType;
    }
    return EXTENSION_MIME_TYPES[extensionOf(fileName)] ?? "application/octet-stream";
};

/** The decoded byte length of a (prefix-free) base64 string, without allocating it. */
export const base64ByteLength = (base64: string): number => {
    const len = base64.length;
    if (len === 0) {
        return 0;
    }
    const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
    return Math.floor((len * 3) / 4) - padding;
};
